import { useContext, useState } from 'react';

import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Pick, Player, SelectOption } from 'src/models';
import logging from 'src/services/logging';

import {
  Alert,
  Backdrop,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import AutocompleteWrapper from 'src/components/Picks/AutocompleteWrapper';
import TextFieldWrapper from 'src/components/Picks/TextFieldWrapper';
import PlayersAutocomplete from 'src/components/Picks/PlayersAutocomplete';
import { db } from 'src/services/firebase-service';
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { CycleContext } from 'src/context/CycleContext';
import useUser from 'src/hooks/useUser';

const FORM_VALIDATION = Yup.object().shape({
  score1: Yup.number()
    .required('Required')
    .test('valid-score', 'Switch values', (value, context) =>
      value ? context.parent.score2 < value : false
    ),
  score2: Yup.number().required('Required'),
  homePlayer: Yup.object().shape({
    id: Yup.number().required(),
    fullName: Yup.string().required(),
  }),
  awayPlayer: Yup.object().shape({
    id: Yup.number().required(),
    fullName: Yup.string().required(),
  }),
  numOfGames: Yup.number().min(4).max(7).required('Required'),
  team: Yup.object().shape({
    id: Yup.number().required(),
    name: Yup.string().required(),
  }),
});

export type NewPickFormProps = {
  picks: Pick[];
  gamePk: number;
  playoffRound: number;
  playoffSeriesLetter: string;
  season: number;
  homeTeam: string;
  awayTeam: string;
  homePlayers: Player[];
  awayPlayers: Player[];
  teams: SelectOption[];
  startTime: number;
  numOfSteps: number;
};

const NewPickForm = ({
  picks,
  gamePk,
  playoffRound,
  playoffSeriesLetter,
  season,
  homeTeam,
  awayTeam,
  homePlayers,
  awayPlayers,
  teams,
  startTime,
  numOfSteps,
}: NewPickFormProps): JSX.Element => {
  const cycle = useContext(CycleContext);
  const { dbUser, fetchingUser } = useUser();
  const [submitting, setSubmitting] = useState(false);
  const [pickSaved, setPickSaved] = useState(false);
  const pick = picks.filter((p) => p.gamePk === gamePk)[0];
  const now = Date.now();
  const isGameStarted = now > startTime;
  // const isGameStarted = false;

  const handleOnSubmit = async (formValues: any) => {
    const currentPicks = {
      gamePk,
      playoffRound,
      playoffSeriesLetter,
      season,
      ...formValues,
    };

    try {
      logging.info({ currentPicks }, 'NewPickForm');
      setSubmitting(true);
      const q = query(
        collection(db, 'hokage'),
        where('isCurrentSeason', '==', true)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs[0];

      let isRoundOpen = 0;
      switch (cycle.activeRound) {
        case 1:
          isRoundOpen = data.data().roundOneStatus;
          break;
        case 2:
          isRoundOpen = data.data().roundTwoStatus;
          break;
        case 3:
          isRoundOpen = data.data().roundThreeStatus;
          break;
        case 4:
          isRoundOpen = data.data().roundFourStatus;
          break;
        default:
          break;
      }

      if (isRoundOpen && dbUser && !isGameStarted) {
        await setDoc(doc(db, 'picks', `${gamePk}-${dbUser?.uid}`), {
          ...currentPicks,
          uid: dbUser?.uid,
        });
        setSubmitting(false);
        setPickSaved(true);
      } else {
        // user tries to submit pick edits after game started or round is closed. force refresh
        setSubmitting(false);
        setPickSaved(true);
        // TODO: come up with a better way rather than just forced refresh
        window.location.reload();
      }
    } catch (error) {
      logging.error(error, 'NewPickForm');
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setPickSaved(false);
  };

  if (fetchingUser) {
    return (
      <Backdrop open={fetchingUser}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  }

  return (
    <Card raised>
      <Divider />
      <CardContent>
        <Formik
          enableReinitialize
          initialValues={{
            score1: pick?.score1 || '',
            score2: pick?.score2 || '',
            homePlayer: pick?.homePlayer || null,
            awayPlayer: pick?.awayPlayer || null,
            numOfGames: pick?.numOfGames || '',
            team: pick?.team || null,
          }}
          validationSchema={FORM_VALIDATION}
          onSubmit={handleOnSubmit}
        >
          {({ values }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item container xs={12} spacing={1}>
                  <Grid item>
                    <Typography>Score</Typography>
                  </Grid>
                  <Grid item>
                    <Tooltip title='Left number must be greater than right number, eg. 4-2'>
                      <InfoIcon fontSize='small' />
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid item container spacing={1} justifyContent='flex-start'>
                  <Grid item xs={3} md={2}>
                    <TextFieldWrapper
                      name='score1'
                      min={0}
                      max={10}
                      value={values.score1}
                      disabled={isGameStarted}
                    />
                  </Grid>
                  <Grid item xs={3} md={2}>
                    <TextFieldWrapper
                      name='score2'
                      min={0}
                      max={10}
                      value={values.score2}
                      disabled={isGameStarted}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography>Game Winner</Typography>
                </Grid>
                <Grid item xs={12}>
                  <PlayersAutocomplete
                    id='homePlayer'
                    options={homePlayers}
                    label={`${homeTeam} Player`}
                    player={values.homePlayer}
                    disabled={isGameStarted}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PlayersAutocomplete
                    id='awayPlayer'
                    options={awayPlayers}
                    label={`${awayTeam} Player`}
                    player={values.awayPlayer}
                    disabled={isGameStarted}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography>Number of Games</Typography>
                </Grid>
                <Grid item xs={3}>
                  <TextFieldWrapper
                    name='numOfGames'
                    min={4}
                    max={7}
                    value={values.numOfGames}
                    disabled={isGameStarted}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography>Series Winner</Typography>
                </Grid>
                <Grid item xs={12}>
                  <AutocompleteWrapper
                    id='team'
                    options={teams}
                    label='Team'
                    team={values.team}
                    disabled={isGameStarted}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant='contained'
                    fullWidth={true}
                    type='submit'
                    disabled={isGameStarted || submitting}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          open={pickSaved}
          autoHideDuration={5000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity='success'
            variant='filled'
            sx={{ width: '100%' }}
          >
            <Typography variant='button'>Pick saved!</Typography>
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default NewPickForm;
